/**
 * Wire types for forwards v2, mirroring the Pydantic response models one for one.
 *
 * Field names are the backend's `snake_case` exactly as `model_dump(mode="json")` emits them:
 * `Decimal` arrives as a string, `datetime`/`date` as ISO strings, and a `dict[int, int]` as an
 * object with string keys.
 *
 * Sources: `app/forwards2/mandate_service.py`, `availability_service.py`, `eligibility_service.py`;
 * the timeline block mirrors the contract pinned in plan Appendix C
 * (`GET /api/forwards/mandates/timeline`, built by WP-08).
 */

import type { PeriodWidth } from "@/forwards2/periodUtils";

/**
 * `forward_mandate_models.MandateListingKind`. A mandate's entry width is a `PeriodWidth` (`periodUtils`),
 * null exactly for `pay_as_produced`.
 */
export type MandateListingKind = "one_off" | "recurring" | "pay_as_produced";
export type MandateDeviceScope = "all_devices" | "allowlist";

/**
 * `forward_mandate_models.ContangoCondition` — when the producer lets a mandate's volume sell, against the
 * spot reference. Advisory: the trader checks the reference by hand. The 20% threshold is discouraged.
 */
export type ContangoCondition = "none" | "any" | "at_least_20_percent";

export const CONTANGO_CONDITIONS: readonly ContangoCondition[] = [
  "none",
  "any",
  "at_least_20_percent",
];

/**
 * `forward_mandate_models.SalesStrategy` — how a mandate's listed volume is released for sale over each
 * production year's sales span: at once, in two equal parts, or in four. Null exactly for a
 * pay-as-produced mandate, which has no volume to release.
 */
export type SalesStrategy = "asap" | "half_yearly" | "quarterly";

export const SALES_STRATEGIES: readonly SalesStrategy[] = [
  "asap",
  "half_yearly",
  "quarterly",
];

/**
 * `sales_strategy.SalesPlan` — a mandate's sales strategy: both choices, always together.
 *
 * @property early_share_pct - The % of each production year released during the year two before
 *   production; the rest is released during the year before. 0 sells next year's production only.
 */
export interface SalesPlan {
  strategy: SalesStrategy;
  early_share_pct: number;
}

/** `sales_strategy.EARLY_SHARE_PRESETS` — the only early shares a mandate save accepts. */
export const EARLY_SHARE_PRESETS: readonly number[] = [0, 25, 50, 75];

/** What a new mandate starts with: ops' practice of four sales in the year before production. */
export const DEFAULT_SALES_PLAN: Readonly<SalesPlan> = {
  strategy: "quarterly",
  early_share_pct: 0,
};

/** `sales_strategy.Release` — the next window to open and the MWh it adds over the periods asked about. */
export interface SalesRelease {
  opens_on: string;
  added_mwh: number;
}

/**
 * `availability_service.PeriodCapacity` — what a new pull may draw in one period.
 *
 * @property sellable_mwh - Within the mandate's sales strategy
 * @property available_mwh - Past it: the ceiling a super admin's override lifts the cap to
 * @property held_back_mwh - What the strategy holds back between the two
 * @property next_release - The earliest window that releases more there; null once all of it is
 */
export interface PeriodCapacity {
  sellable_mwh: number;
  available_mwh: number;
  held_back_mwh: number;
  next_release: SalesRelease | null;
}

/**
 * The mandate an overriding write took beyond its sales strategy — the API's counterpart of the
 * override banner. Every capped write's response carries it at its top level.
 */
export interface BeyondStrategyRow {
  mandate_id: number;
  mandate_name: string;
  volume_mwh: number;
  /** The strategy the write went past, as it stood at the pull. */
  sales_plan: SalesPlan;
}

export type DealStatus = "draft" | "confirmed" | "completed" | "cancelled";

/** `money_models.SalesTransactionStatus` — the lifecycle of the sales transaction behind a delivery. */
/**
 * `SalesTransactionStatus`, in lifecycle order; the upcoming-deliveries card's status filter offers
 * them all. `tests/forwards2/test_transaction_status_parity.py` holds it to the backend enum.
 */
export const TRANSACTION_STATUSES = [
  "DRAFT",
  "PENDING_BUYER_DETAILS",
  "PENDING_CONTRACTS",
  "READY_TO_TRANSFER",
  "DELIVERED",
  "COMPLETED",
] as const;

export type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

/** Matches the backend's own default for `GET /upcoming-deliveries` when `transaction_status[]` is omitted. */
export const DEFAULT_TRANSACTION_STATUS_FILTERS: TransactionStatus[] =
  TRANSACTION_STATUSES.filter((status) => status !== "COMPLETED");

/** `eligibility_service.CountryTier` — the three-tier country verdict plus the unresolved case. */
export type CountryTier =
  "standard_aib_gc" | "aib_outside_standard" | "pl_be" | "non_aib" | "unknown";

/**
 * What `ForwardsStatusBadgeComponent` can render: a deal status plus the mandate states that read as
 * one to a user but are separate columns on the wire (`archived_at`, `is_escrow_remaining`, PAP).
 */
export type ForwardsBadgeStatus =
  DealStatus | "archived" | "escrow_remaining" | "pay_as_produced";

/** `eligibility_service.ECOLABEL_KEYS`. */
export const ECOLABEL_KEYS = ["ekoenergy", "okpower", "okpower_new"] as const;

// --- eligibility ------------------------------------------------------------------------------

export interface EcolabelIndicator {
  key: string;
  label: string;
  all_devices_qualify: boolean;
  failing_device_ids: number[];
  /** Last production year the whole mandate still qualifies; null when a device never qualifies. */
  expires_after_year: number | null;
}

export interface IndicatorBlocker {
  dimension: string;
  detail: string;
  device_ids: number[];
}

/** `eligibility_service.MandateSupport`: `unsupported` only when every device is known to be unsupported; unknown support reads as `mixed`. */
export const MANDATE_SUPPORTS = ["unsupported", "mixed"] as const;
export type MandateSupport = (typeof MANDATE_SUPPORTS)[number];

/**
 * `eligibility_service.CodPreference` — RE100's commissioning-age preference, judged by the server
 * for the current UTC year. An age check only; it passes only when every device has a COD on or
 * after `cutoff`.
 *
 * @property cutoff - ISO date, 1 January of the earliest commissioning year still preferred
 * @property older_device_ids - Devices commissioned before `cutoff`; devices without a COD are not listed
 */
export interface CodPreference {
  claim_year: number;
  cutoff: string;
  passes: boolean;
  older_device_ids: number[];
}

export interface EligibilityIndicator {
  country_tier: CountryTier;
  countries: string[];
  technology_mix: string[];
  oldest_cod: string | null;
  cod_preference: CodPreference;
  support: MandateSupport;
  ecolabels: EcolabelIndicator[];
  blockers: IndicatorBlocker[];
}

// --- mandates ------------------------------------------------------------------------------------

/** `availability_service.CommitmentSegment` — `deal_id`/`deal_name` only for viewers of the deal. */
export interface CommitmentSegment {
  commitment_id: number;
  slice_id: number;
  volume_mwh: number;
  escrowed_mwh: number;
  deal_status: DealStatus;
  window_end: string | null;
  deal_id: number | null;
  deal_name: string | null;
}

/** `availability_service.PeriodSummary` — `escrowed` is part of `committed`, never a sibling. */
export interface PeriodSummary {
  period_label: string;
  listed: number;
  committed: number;
  escrowed: number;
  available: number;
  infeasible: boolean;
  segments: CommitmentSegment[];
}

/** One `(period, volume)` row of a one-off mandate, as the editor sends it. */
export interface ListingWrite {
  period_start: string;
  period_end: string;
  volume_mwh: number;
}

/**
 * The production a recurring or pay-as-produced mandate may sell to new deals, in whole months. Both
 * keys are required on every save; null leaves that end open.
 *
 * @property production_from - The first day of a month (`YYYY-MM-01`)
 * @property production_until - The last day of a month (`YYYY-MM-31`, `YYYY-02-28`, …)
 */
export interface ProductionRange {
  production_from: string | null;
  production_until: string | null;
}

/**
 * The mandate sells its devices' whole output, so it states no volumes and no width to state them at.
 * A new contract's window must lie within the production range.
 */
export interface PayAsProducedListing extends ProductionRange {
  kind: "pay_as_produced";
}

/**
 * Volumes as a per-slot template, repeating every year. A period is listed only while it lies
 * wholly inside the production range.
 */
export interface RecurringListing extends ProductionRange {
  kind: "recurring";
  entry_width: PeriodWidth;
  /** Slot number (1-12 months, 1-4 quarters, 1-2 half-years, 1 year) → MWh; string keys on the wire. */
  volumes: Record<string, number>;
  /** Required on every save, never defaulted by the backend: a body without it is refused. */
  sales_plan: SalesPlan;
}

/** Volumes as explicit rows, each one calendar period of the mandate's entry width. */
export interface OneOffListing {
  kind: "one_off";
  entry_width: PeriodWidth;
  listings: ListingWrite[];
  /** Required on every save, never defaulted by the backend: a body without it is refused. */
  sales_plan: SalesPlan;
}

/**
 * What a mandate lists, as one of three exclusive policies — `kind` picks the branch, and a listed kind
 * carries the sales strategy that releases its volume. A mandate is read back with the same policy it
 * is written with (`MandateResponse.listing`).
 *
 * The backend refuses any mixture outright, so a pay-as-produced body carrying volumes or a
 * recurring one carrying one-off rows has no spelling: build the policy the mode actually is.
 */
export type MandateListingWrite =
  PayAsProducedListing | RecurringListing | OneOffListing;

/** The whole editor form. `PUT /mandates/<id>` replaces the mandate with this, so always send all of it. */
export interface MandateWriteRequest {
  /** Required for replacement; omitted or null for creation and unsaved previews. */
  version?: number | null;
  name: string;
  description: string | null;
  device_scope: MandateDeviceScope;
  member_company_ids: number[];
  allowlist_device_ids: number[];
  /** Decimal on the wire: send a plain decimal string, or null for no floor. */
  minimum_price: string | null;
  contango_condition: ContangoCondition;
  archive_on_delivery: boolean;
  /**
   * Staff's choice to show the editor's every listing shape; otherwise it offers only a recurring
   * listing's quarters. Required on every save, like the listing's `sales_plan`.
   */
  advanced_listing: boolean;
  /** What the mandate lists; the stored listing kind, entry width and sales strategy are derived from it. */
  listing: MandateListingWrite;
}

export interface MandateResponse {
  id: number;
  version: number;
  name: string;
  description: string | null;
  /** The policy the mandate was saved with; null in the escrow-remaining view, which may not see it. */
  listing: MandateListingWrite | null;
  device_scope: MandateDeviceScope;
  minimum_price: string | null;
  contango_condition: ContangoCondition;
  archive_on_delivery: boolean;
  advanced_listing: boolean;
  archived_at: string | null;
  member_company_ids: number[];
  allowlist_device_ids: number[];
  created_at: string;
  updated_at: string;
  has_commitments: boolean;
}

export interface MandateWriteResult {
  mandate: MandateResponse;
  warnings: string[];
}

export interface MandateMemberRow {
  company_id: number;
  company_name: string;
  /** ISO 3166-1 alpha-2; null where the company has none on record. */
  country: string | null;
  forward_sales_enabled: boolean;
  allocation_ready: boolean;
}

/** Every candidate device of the member companies; `in_mandate` is the allowlist checkbox state. */
export interface MandateDeviceRow {
  device_id: number;
  device_name: string | null;
  company_id: number;
  company_name: string | null;
  country: string | null;
  technology_code: string | null;
  energy_type: string | null;
  cod: string | null;
  support: boolean | null;
  ecolabel_approved: boolean | null;
  /** `ECOLABEL_KEYS` the device qualifies for this production year, as the eligibility card judges them; empty when none. */
  ecolabels: string[];
  /** Why the device cannot reach a Soldera escrow account; null when it can. */
  escrowable_reason: string | null;
  /** False when the backend cannot project this device; its zeroes are unknown supply, not a forecast. */
  production_estimate_available: boolean;
  /** Period label → estimated MWh, net of PPA; zero-filled when projection is unavailable. */
  estimated_mwh_by_period: Record<string, number>;
  in_mandate: boolean;
  pap_locked: boolean;
  /**
   * Forward mandates other than the one being edited whose claim on this device still stands: live
   * ones, plus archived ones that still owe volume.
   */
  mandate_count: number;
  /** Active PPAs covering this device; a PPA is served ahead of forwards in the waterfall. */
  ppa_count: number;
  /**
   * Set on an allowlisted device the registry sync has taken away: deleted, or no longer
   * registered. It covers nothing and is listed only so it can be unticked; null otherwise.
   */
  stale_reason: MandateDeviceStaleReason | null;
}

export type MandateDeviceStaleReason = "deleted_by_registry" | "not_registered";

export interface MandateCapabilities {
  can_edit: boolean;
  /** False for a super admin acting as themselves on a client's mandate: they manage the account to change its companies. */
  can_change_members: boolean;
  can_delete: boolean;
  delete_blocked_reason: string | null;
  can_archive: boolean;
  can_unarchive: boolean;
  is_admin: boolean;
}

/** What a company left behind in a mandate it was removed from: volumes only, never deal identity. */
export interface EscrowRemainingRow {
  period_label: string;
  escrowed_mwh: number;
  commitment_status: DealStatus;
  window_end: string | null;
}

export interface MandateDetailResponse {
  mandate: MandateResponse;
  members: MandateMemberRow[];
  devices: MandateDeviceRow[];
  periods: PeriodSummary[];
  /** Null for an escrow-remaining viewer, which sees no devices to judge. */
  eligibility: EligibilityIndicator | null;
  infeasible_periods: string[];
  capabilities: MandateCapabilities;
  /**
   * Null for a member or an admin; the rows a former member left behind otherwise.
   *
   * Non-null means the whole response is the reduced one: the mandate keeps its name, width and
   * archival state, and every other field is empty — escrow grants sight of the volume left
   * behind, not of the mandate.
   */
  escrow_remaining: EscrowRemainingRow[] | null;
}

export interface PeriodPreview {
  period_label: string;
  listed: number;
  committed: number;
  free: number;
  /** Estimated production for the period, net of PPA — the basis of the %-to-MWh conversion. */
  estimated_mwh: number;
  /** `listed / estimated_mwh`, null when nothing is estimated. */
  utilisation: number | null;
}

export type SalesWindowState = "passed" | "current" | "upcoming";

/**
 * `sales_strategy.ScheduleWindow` — one auction of a sales year under the previewed strategy.
 *
 * @property opens_on - ISO date the auction's release opens (a UTC day), the first of a quarter
 * @property added_mwh - What it adds to the release over the sales year's listings
 */
export interface SalesAuction {
  opens_on: string;
  added_mwh: number;
  state: SalesWindowState;
}

/**
 * `sales_strategy.SalesTimelineRow` — one listing of the sales year.
 *
 * @property added_mwh - What each auction adds to this listing's release, aligned with the timeline's `auctions`
 */
export interface SalesTimelineRow {
  period_label: string;
  added_mwh: number[];
}

/**
 * `mandate_form_service.CommittedAboveRelease` — a period committed above what the strategy releases
 * there today. The commitments stand; only new sales are held to the strategy.
 */
export interface CommittedAboveRelease {
  period_label: string;
  committed_mwh: number;
  released_today_mwh: number;
}

/**
 * `sales_strategy.SalesTimelineGroup` — the listings of one production year a sales year releases.
 *
 * @property early - The sales year releases the early share and the next one the rest; otherwise it
 *   releases the rest, after the early share went the year before
 * @property share_pct - The % of the production year the sales year releases; 100 for a mandate that
 *   does not sell ahead
 */
export interface SalesTimelineGroup {
  production_year: number;
  early: boolean;
  share_pct: number;
  rows: SalesTimelineRow[];
}

/**
 * `sales_strategy.SalesTimeline` — one sales year of the previewed strategy: its auctions, and
 * every listing they release, by production year. A mandate that sells ahead releases two production
 * years in each: the rest of the next one and the early share of the one after.
 */
export interface SalesTimeline {
  sales_year: number;
  auctions: SalesAuction[];
  groups: SalesTimelineGroup[];
}

/**
 * `mandate_form_service.ReleaseChange` — how a save would change what one production year has released
 * for sale today, over the form's listing.
 *
 * @property delta_mwh - The form's strategy minus the saved one; negative holds volume back
 */
export interface ReleaseChange {
  production_year: number;
  delta_mwh: number;
}

/** `mandate_form_service.OverCommittedPeriod` — a still-selling period with more committed than listed. */
export interface OverCommittedPeriod {
  period_label: string;
  listed: number;
  committed: number;
}

export interface MandatePreviewResponse {
  periods: PeriodPreview[];
  eligibility: EligibilityIndicator;
  blockers: string[];
  warnings: string[];
  /** Includes the backed periods `periods` leaves out because the form no longer lists them. */
  over_committed: OverCommittedPeriod[];
  /** Device id (string key) → estimated MWh over the previewed periods. */
  estimated_by_device: Record<string, number>;
  /** Suggested only when the mandate's widest observed issuance cadence is a half-year. */
  recommended_entry_width: PeriodWidth | null;
  /** This sales year and the next, where the form lists volume they release; empty for pay-as-produced. */
  sales_schedule: SalesTimeline[];
  /**
   * The narrowest periods of the schedule's production years committed above today's release, judged
   * as the cap judges a take: per listing and at every period around it.
   */
  committed_above_release: CommittedAboveRelease[];
  /** Empty for a new mandate or an unchanged strategy. */
  release_changes: ReleaseChange[];
}

/**
 * `mandate_read_service.MandateCommitmentRow` — one backed slice, or one pay-as-produced commitment over its
 * whole contract. Anonymised (`deal_id`/`deal_name` null) unless the viewer may open the deal.
 *
 * @property row_id - Unique across both kinds of row; a slice id and a commitment id can coincide
 * @property slice_id - Null for a pay-as-produced commitment, which backs no slice
 * @property period_label - The contract's months (`2026-05 → 2027-04`) for a pay-as-produced row
 * @property volume_mwh - The sum of the monthly nominations for a pay-as-produced row
 * @property committed_at - When the commitment was made; a migrated one keeps its v1 reservation's date
 */
export interface MandateCommitmentRow {
  row_id: string;
  commitment_id: number;
  slice_id: number | null;
  period_label: string;
  volume_mwh: number;
  escrowed_mwh: number;
  window_start: string | null;
  window_end: string | null;
  status: DealStatus;
  deal_id: number | null;
  deal_name: string | null;
  committed_at: string;
}

/**
 * `mandate_read_service.ActiveMandateRow` — a mandate the producer dashboard lists for its companies:
 * not archived, and either its production range has not ended (no `production_until`, or not yet
 * past) or it still owes deliveries.
 */
export interface ActiveMandateRow {
  mandate_id: number;
  name: string;
  listing_kind: MandateListingKind;
}

export interface MandateListRow {
  mandate_id: number;
  name: string;
  listing_kind: MandateListingKind;
  /** Null exactly for a pay-as-produced mandate. */
  entry_width: PeriodWidth | null;
  archived_at: string | null;
  member_count: number;
  device_count: number;
  is_escrow_remaining: boolean;
}

// --- timeline (WP-08) -------------------------------------------------------------------------

/** The three-state status filter of the mandates timeline. */
export const MANDATE_STATUS_FILTERS = [
  "active",
  "archived_open",
  "archived_settled",
] as const;
export type MandateStatusFilter = (typeof MANDATE_STATUS_FILTERS)[number];

/** Defaults per Appendix D.1 row 1b: settled archived mandates are hidden until asked for. */
export const DEFAULT_MANDATE_STATUS_FILTERS = [
  "active",
  "archived_open",
] as const satisfies readonly MandateStatusFilter[];

export interface TimelineQuery {
  year: number;
  status: MandateStatusFilter[];
  company_ids: number[];
}

export interface BandCell {
  quarter: string;
  listed: number;
  committed: number;
  escrowed: number;
  available: number;
}

/**
 * A commitment inside a timeline cell.
 *
 * Wider than `CommitmentSegment` above: the timeline aggregates monthly slices into their quarter,
 * so `slice_id` is null for a segment that stands for more than one slice.
 */
export interface TimelineCommitmentSegment {
  commitment_id: number;
  slice_id: number | null;
  volume_mwh: number;
  escrowed_mwh: number;
  deal_status: DealStatus;
  window_end: string | null;
  deal_id: number | null;
  deal_name: string | null;
}

export interface MandateCell {
  /** Label of the first quarter the cell covers — its position in the grid. */
  quarter: string;
  /** The period the figures are for: the half-year/year on a span cell, otherwise the quarter. */
  period_label: string;
  /** 1 normally, 2 for a half-year span, or 4 for a year span. */
  span: number;
  listed: number;
  committed: number;
  escrowed: number;
  available: number;
  /** What the mandate's sales strategy lets it sell today; never more than `available`. */
  sellable_now: number;
  segments: TimelineCommitmentSegment[];
}

/**
 * One pay-as-produced contract on a mandate. Renewals are legal, so a mandate can hold several inside
 * one window — see `MandateRow.pap_contracts`.
 */
export interface PapRow {
  window_start: string;
  window_end: string;
  escrowed: number;
  delivered: number;
}

export interface MandateRow {
  mandate_id: number;
  name: string;
  listing_kind: MandateListingKind;
  /** Null exactly for a pay-as-produced mandate. */
  entry_width: PeriodWidth | null;
  /** A mandate this account only has escrow left in — view-only, own escrow per quarter. */
  is_escrow_remaining: boolean;
  is_archived: boolean;
  member_count: number;
  device_count: number;
  infeasible_periods: string[];
  cells: MandateCell[];
  /** Every contract overlapping the visible quarters, earliest first. */
  pap_contracts: PapRow[];
}

/** A mandate containing one of the account's companies whose other members are not accessible. */
export interface RestrictedMandateRow {
  mandate_id: number;
  name: string;
}

export interface TimelineCompany {
  company_id: number;
  name: string;
}

export interface TimelineResponse {
  /** The four quarter labels of the requested `year`. */
  window: string[];
  /** One entry per quarter; wider spans are divided evenly across their quarters. */
  band: BandCell[];
  band_note: string | null;
  rows: MandateRow[];
  restricted_mandates: RestrictedMandateRow[];
  /** The company filter's choices: the companies of the session's scope. */
  companies: TimelineCompany[];
  /** Whether the account's companies are or were in any mandate, archived ones included, whatever the filters. */
  has_mandates: boolean;
}

// --- overview (WP-08) -------------------------------------------------------------------------

/** Why a row is on the "Needs attention" card. */
export type AttentionKind =
  | "stale_draft"
  | "open_slices"
  | "escrow_shortfall"
  | "producer_price_missing"
  | "price_conflict"
  | "delivery_unpriced"
  | "free_escrow"
  | "infeasible"
  | "coarse_issuance"
  | "allocation_not_ready"
  | "mandate_escrow_missing"
  | "contango_discouraged";

export interface PeriodShortfall {
  period_label: string;
  mwh: number;
}

/** The single action an attention row offers. `id` is a mandate id for `open_mandate`, a deal id otherwise. */
export type AttentionCtaKind =
  "open_mandate" | "open_deal" | "review_draft" | "deliver" | "return_escrow";

export interface AttentionCta {
  kind: AttentionCtaKind;
  id: number;
  delivery_id: number | null;
}

/**
 * One row of the attention card: what is wrong, with which mandate or deal, and the one thing to do.
 *
 * `reason` is the backend's own sentence. The client re-renders the kinds it knows from `mwh`,
 * `period_label`, `since` and `shortfalls` so the copy is translated; `reason` is the fallback for
 * anything else.
 *
 * @property mwh - On `infeasible`, the total over `shortfalls`.
 * @property since - For `escrow_shortfall`, `producer_price_missing` and `price_conflict`, the delivery's
 *   window end; for `delivery_unpriced`, the window end of the unpriced delivery due first.
 * @property shortfalls - Set on `infeasible` only: each period the mandate is expected to fall short in,
 *   in order; empty otherwise.
 * @property listed_width - Set on `coarse_issuance` only: the mandate's listing width.
 * @property issuance_by_region - Set on `coarse_issuance` only: each registry region issuing wider than the mandate
 *   lists, with the period it issues for, sorted by region; empty otherwise.
 * @property issuance_width - Set on `coarse_issuance` only: the widest of those, which the mandate must list at.
 */
export interface AttentionRow {
  kind: AttentionKind;
  /** Deal or mandate name. */
  subject: string;
  reason: string;
  mwh: number | null;
  period_label: string | null;
  since: string | null;
  shortfalls: PeriodShortfall[];
  /**
   * Set on `allocation_not_ready` (staff-only): the mandate's members that nightly allocation skips;
   * on `price_conflict`: the company selling through differently priced mandates.
   */
  company_names: string[];
  /** Set on `price_conflict` only: the mandates that company sells through on the delivery. */
  mandate_names: string[];
  /** Set on `delivery_unpriced` only: how many of the deal's deliveries have no buyer price. */
  delivery_count: number | null;
  listed_width: PeriodWidth | null;
  issuance_by_region: Record<string, PeriodWidth>;
  issuance_width: PeriodWidth | null;
  cta: AttentionCta;
}

/** Escrow a delivery still lacks, grouped by the device countries of the mandates that committed it. */
export interface MissingByCountry {
  /** One entry for a single-country mandate; several for a mandate whose devices span countries. */
  countries: string[];
  mwh: number;
}

/** A buyer sales invoice of a delivery's transaction; `created_on` is an ISO date (UTC). */
export interface BuyerInvoiceRef {
  invoice_number: string;
  created_on: string;
}

/**
 * One delivery of the upcoming-deliveries card. `buyer_name`, `volume_mwh`, `unit_price` and
 * `buyer_invoices` are not displayed; they are what the deliveries spreadsheet carries.
 *
 * @property deal_status - A cancelled deal is listed only for what it shipped before it was cancelled.
 * @property buyer_name - The transaction's counterparty: the deal's buyer until it ships, what it shipped to after.
 * @property volume_mwh - What the buyer is billed for: the shipped amount once shipped, the plan or target until then.
 * @property unit_price - The buyer price as a decimal string; null while unpriced.
 * @property buyer_invoices - Every uncanceled buyer sales invoice of the transaction, oldest first; normally at most one.
 */
export interface UpcomingDeliveryRow {
  delivery_id: number;
  deal_id: number;
  deal_name: string;
  deal_status: DealStatus;
  buyer_name: string | null;
  /** A monthly delivery of a pay-as-produced deal rather than a fixed-volume one. */
  is_pay_as_produced: boolean;
  window_start: string | null;
  window_end: string;
  /** Days until `window_end`; negative when the window has already closed. */
  days_left: number;
  /** Period labels of the delivery's slices; a pay-as-produced month's own month. */
  periods: string[];
  escrowed_mwh: number;
  /** A pay-as-produced month's nominated target: monitored, never owed. */
  planned_mwh: number;
  /** Always empty for a pay-as-produced month, which owes no fixed volume. */
  missing_by_country: MissingByCountry[];
  volume_mwh: number;
  unit_price: string | null;
  buyer_invoices: BuyerInvoiceRef[];
  transaction_status: TransactionStatus;
  sales_transaction_id: number;
}

export interface OverviewResponse {
  needs_attention: AttentionRow[];
}

/**
 * `forward_year_totals_service.ForwardYearTotal`: what is sold forward of one production year.
 *
 * @property priced_mwh - The year's sold volume that has a buyer price, so `priced_mwh` times `avg_price` is the
 *   value of what is sold.
 * @property avg_price - The buyer price weighted by `priced_mwh`, a decimal string to three places; null when
 *   nothing of the year is priced yet.
 * @property unpriced_mwh - Sold volume whose delivery has no buyer price yet, left out of both of the above.
 */
export interface ForwardYearTotal {
  year: number;
  priced_mwh: number;
  avg_price: string | null;
  unpriced_mwh: number;
}

export interface ForwardYearTotalsResponse {
  years: ForwardYearTotal[];
}

/** `upcoming_deliveries_service.UpcomingDeliveriesResponse`. */
export interface UpcomingDeliveriesResponse {
  rows: UpcomingDeliveryRow[];
}

/**
 * The upcoming-deliveries card's filters.
 *
 * @property transactionStatus - Empty means the endpoint's default, every status but completed. Draft deals
 *   are never listed, nor a cancelled deal's unshipped deliveries, whatever is asked.
 * @property windowEndFrom - Inclusive ISO date; null leaves that end open.
 * @property windowEndTo - Inclusive ISO date; null leaves that end open.
 * @property dealName - Case-insensitive substring; empty matches every deal.
 */
export interface UpcomingDeliveriesQuery {
  transactionStatus: TransactionStatus[];
  windowEndFrom: string | null;
  windowEndTo: string | null;
  dealName: string;
}

/** `mandates_attention_service.MandatesAttentionResponse` — the mandates panel's Needs attention, narrowed by `company_ids[]`. */
export interface MandatesAttentionResponse {
  rows: AttentionRow[];
}

// --- managed deals table (WP-08) --------------------------------------------------------------

export type DealType = "fixed_volume" | "pay_as_produced";

/** The deals table's status filter offers every deal status; the default is the two live ones. */
export const DEAL_STATUS_FILTERS = [
  "draft",
  "confirmed",
  "completed",
  "cancelled",
] as const;

/** Matches the backend's own default for `GET /deals` when `status[]` is omitted. */
export const DEFAULT_DEAL_STATUS_FILTERS: DealStatus[] = ["draft", "confirmed"];

/** `deals_table_service.DEAL_FILTER_TEXT_MAX`: a longer name, mandate or company filter is a 400. */
export const DEAL_FILTER_TEXT_MAX = 200;

export interface DealRow {
  deal_id: number;
  name: string;
  buyer: string | null;
  type: DealType;
  /** Distinct quarter labels of the deal's slices, sorted. */
  quarters: string[];
  /** The stored deal volume, not the sum of its commitments (plan §1.1 "Deal volume column"). */
  volume_mwh: number | null;
  deliveries_completed: number;
  deliveries_total: number;
  status: DealStatus;
  created_at: string;
  /** A draft left unconfirmed for long enough that it is worth chasing. */
  is_stale: boolean;
  /**
   * Total of all slice volume, open ones included — what the plan now sells. Against `volume_mwh`
   * it is the deal-volume drift; null for a pay-as-produced deal, which holds no slices.
   */
  planned_mwh: number | null;
  /**
   * Total backed slice volume, 0 for a fixed-volume deal nothing backs yet. `planned_mwh` less this
   * is the deal's open volume; null only for a pay-as-produced deal, which holds no slices.
   */
  committed_mwh: number | null;
  /**
   * The lowest and highest buyer price (a decimal string, €/MWh) across the deal's deliveries,
   * voided ones left out; both null when no delivery has a price yet.
   */
  price_min: string | null;
  price_max: string | null;
  /** At least one backing mandate has sold more than its devices are expected to produce. */
  is_infeasible: boolean;
}

export interface DealsTableResponse {
  rows: DealRow[];
}

/** `name`, `mandate` and `company` are case-insensitive substring matches; empty means unfiltered. */
export interface DealsQuery {
  status: DealStatus[];
  name: string;
  mandate: string;
  company: string;
}

// --- picker (WP-05) ---------------------------------------------------------------------------

/**
 * `eligibility_service.CriterionName` — the dimension a mandate failed on.
 *
 * `escrowable` and `devices` are not criteria the trader set: the first means the mandate's devices
 * cannot reach a Soldera escrow account, the second that the mandate covers no live device at all.
 */
export type CriterionName =
  | "countries"
  | "technologies"
  | "cod"
  | "support"
  | "ecolabels"
  | "escrowable"
  | "devices";

/** `eligibility_service.FailedCriterion`. `period_label` is set for the time-dependent checks. */
export interface FailedCriterion {
  criterion: CriterionName;
  detail: string;
  period_label: string | null;
  /** The device the failure belongs to; null is the mandate itself ("no devices"). */
  device_id: number | null;
}

/**
 * The deal's product definition, as the criteria step sends it.
 *
 * An empty list is unrestricted, which is why the wire type is a list and never null: the stored
 * criteria come back as `EligibilityCriteriaResponse`, where null is the unrestricted dimension.
 */
export interface EligibilityCriteriaRequest {
  countries: string[];
  technologies: string[];
  cod_from: string | null;
  cod_until: string | null;
  support: boolean | null;
  ecolabels: string[];
}

/**
 * `GET /api/admin/forwards/picker/mandates` — the product definition, the picked range and what the
 * trader has taken so far.
 *
 * The periods are consecutive quarter labels (`2027-Q4`, `2028-Q1`); anything else is refused with a
 * 400. Each mandate covers them at its own width, and a mandate that cannot cover them all is left out. `taken_mandate_ids` steers the warnings and the price hint, which describe the
 * selection rather than the candidates, so it is re-sent whenever the takes change.
 */
export interface PickerQuery {
  periods: string[];
  criteria: EligibilityCriteriaRequest;
  /** UI-only filter: hides mandates whose floor is above the price the trader is aiming for. */
  min_price_filter: string | null;
  taken_mandate_ids: number[];
}

/**
 * `eligibility_service.MandateQualities` — what the picker summary and a delivery's contents group
 * volume by, each held all-or-nothing over the mandate's devices.
 *
 * @property ecolabels - `ECOLABEL_KEYS` the mandate holds over every period judged, all devices included
 */
export interface MandateQualities {
  countries: string[];
  technologies: string[];
  oldest_cod: string | null;
  oldest_cod_age: number | null;
  re100_applicable: boolean | null;
  ecolabels: string[];
  support: MandateSupport;
}

/**
 * One row of the take table.
 *
 * `capacity_total` is the take ceiling, held to what the mandate's sales strategy has released. The
 * per-period caps need not add up to it — a quarter that is already committed elsewhere limits which
 * combinations of months are still available. A mandate whose strategy has released none of its volume
 * yet is not a row but one of the answer's `ineligible` mandates.
 *
 * @property held_back_mwh - Available on top of `capacity_total`, held back by the strategy today
 * @property next_release - The next window to open over the picked periods; null once they are
 *   fully released
 */
export interface PickerMandateRow extends MandateQualities {
  mandate_id: number;
  name: string;
  member_company_names: string[];
  device_count: number;
  minimum_price: string | null;
  contango_condition: ContangoCondition;
  /** Per period of the mandate's cover of the range, at its own width (`2027-Q1`, `2027-H1` or `2027`). */
  capacity_by_period: Record<string, number>;
  capacity_total: number;
  held_back_mwh: number;
  sales_plan: SalesPlan;
  next_release: SalesRelease | null;
  /** The mandate's devices issue in periods wider than its cover of the range. */
  cadence_warning: boolean;
  /** Member companies whose certificates allocation will never reach, by name. */
  allocation_ready_warning: string[];
}

/** A mandate the trader may draw on that this product and range leave out. `reasons` are sentences. */
export interface IneligibleMandate {
  mandate_id: number;
  name: string;
  reasons: string[];
  /**
   * ISO date: the mandate is left out only because its sales strategy has released none of the
   * volume available yet, until this day, and `reasons` is empty. Null for every other mandate.
   */
  held_back_until: string | null;
}

/** `pricing_hint_service.MandateFloor` — one mandate's price conditions. */
export interface MandateFloor {
  mandate_id: number;
  mandate_name: string;
  minimum_price: string | null;
  contango_condition: ContangoCondition;
}

/** `pricing_hint_service.ContangoCheck` — the taken mandates carrying one contango condition. */
export interface ContangoCheck {
  condition: Exclude<ContangoCondition, "none">;
  mandate_names: string[];
}

/**
 * The floor a delivery's price must clear, plus what the trader has to judge by hand.
 *
 * `floor` and `contango_checks` ignore pay-as-produced mandates, whose price is settled when the
 * contract is written; `per_mandate` still reports their conditions. `contango_checks` lists only the
 * conditions some taken mandate carries.
 */
export interface MinimumPriceHint {
  floor: string | null;
  per_mandate: MandateFloor[];
  contango_checks: ContangoCheck[];
}

export interface PickerMandates {
  /** The range this answer describes; the take table renders and confirms from it (RV-picker-10). */
  periods: string[];
  /**
   * The last day of `periods`: the rows' COD age, RE100 applicability and OK-power "new"
   * compatibility, which a device ages out of, are judged at its year.
   */
  qualification_date: string;
  rows: PickerMandateRow[];
  ineligible: IneligibleMandate[];
  product_warnings: string[];
  hint: MinimumPriceHint;
}

/** `POST /api/admin/forwards/deals` — the picker's confirm step. Zero takes are dropped. */
export interface PickerConfirmRequest {
  name: string;
  /** Copied onto every delivery's transaction: printed on the producers' invoices. */
  description: string | null;
  /** Staff only, never copied onto a transaction. */
  internal_notes: string | null;
  criteria: EligibilityCriteriaRequest;
  periods: string[];
  /** Mandate id → MWh; a JSON object, so the keys travel as strings. */
  takes: Record<number, number>;
}

/**
 * What the two create endpoints answer with.
 *
 * Both return the whole `DealResponse`; the picker and the pay-as-produced wizard read only the
 * deal's identity before navigating to its page, which fetches the rest.
 */
export interface DealIdentity {
  id: number;
  name: string;
  type: DealType;
  status: DealStatus;
}

// --- pay-as-produced (WP-07) ------------------------------------------------------------------

/**
 * `POST /api/admin/forwards/pay-as-produced-deals`.
 *
 * The window runs from the first day of a month to the last day of one, and `monthly_targets`
 * carries one `YYYY-MM` entry for every month in it — a missing month is a 400, not a zero.
 * `unit_price` and `volume_tolerance` are decimals as strings; the tolerance is a fraction
 * (`"0.1"` is ±10 %).
 */
export interface PapDealCreateRequest {
  mandate_id: number;
  name: string;
  /** Copied onto every month's transaction: printed on the producers' invoices. */
  description: string | null;
  /** Staff only, never copied onto a transaction. */
  internal_notes: string | null;
  unit_price: string;
  window_start: string;
  window_end: string;
  monthly_targets: Record<string, number>;
  volume_tolerance: string;
}

/** The created deal plus the cadence cautions its devices' countries earn (BE, PL). */
export interface PapDealCreateResult {
  deal: DealIdentity;
  warnings: string[];
}

/**
 * What the wizard says about the chosen mandate before the contract is cut. A new contract's window
 * must lie within its production range.
 */
export interface PapWizardMandateContext extends ProductionRange {
  /** Sentences to show as they are; the rule behind them (which registries issue coarsely) lives on the server. */
  cadence_warnings: string[];
  device_countries: string[];
  minimum_price: string | null;
}

/**
 * One mandate the wizard's mandate field offers, and whether a contract can be cut from it.
 *
 * @property blocked_reason - The create's own refusal, word for word (an English admin sentence);
 *   null when the mandate can take a contract.
 */
export interface PapWizardMandateOption {
  mandate_id: number;
  can_take_contract: boolean;
  blocked_reason: string | null;
}

/**
 * `GET /api/admin/forwards/pay-as-produced-deals/context` — which mandates can take a contract and,
 * once a mandate is chosen, that mandate's advisories. Answered platform-wide, like
 * the admin mandate list, so it works while the admin is managing a client account too.
 *
 * @property mandate_options - Every live pay-as-produced mandate — the rows the admin mandate list answers
 *   the wizard — whichever one is chosen.
 */
export interface PapWizardContext {
  mandate_options: PapWizardMandateOption[];
  mandate: PapWizardMandateContext | null;
}

/**
 * `GET /api/admin/forwards/mandates` — every mandate on the platform, not just the session's own.
 *
 * The pay-as-produced wizard cuts contracts for producers the acting admin is not a member of, so
 * its mandate select cannot use the account-scoped `GET /api/forwards/mandates`. The route filters by
 * `is_pap` and hides archived mandates unless asked for them, so the client re-filters nothing.
 */
export interface AdminMandateListResponse {
  rows: MandateListRow[];
}

// --- delivery lookup (WP-04a) ----------------------------------------------------------------

/**
 * `delivery_lookup.ForwardDeliveryHeader` — the forwards context of a delivery's SalesTransaction.
 *
 * Present on the admin transaction endpoints for a forwards v2 delivery and absent for a spot
 * sale, which is what tells the transactions page which of its fields the deal owns.
 */
export interface ForwardDeliveryHeader {
  deal_id: number;
  deal_name: string;
  delivery_id: number;
  /** Null for an open-ended window — "on or before <window_end>". */
  window_start: string | null;
  window_end: string;
  planned_mwh: number;
  escrowed_mwh: number;
  is_pay_as_produced: boolean;
  /** The deal's and the delivery's staff notes, both set on the deal page; null when there are none. */
  deal_internal_notes: string | null;
  delivery_internal_notes: string | null;
}

// --- deal page (WP-09b) -----------------------------------------------------------------------

/**
 * `app/registry/registration_authorities.DELIVERABLE_AUTHORITIES` — the only authorities a deal may
 * carry, because every delivery derives its registry country from one of them.
 */
export type DealRegistrationAuthority = "AIB" | "REGO" | "RGP";

/** `delivery_plan_warnings.WarningKind` — a plan warning never blocks a save. */
export type PlanWarningKind =
  | "issuance_lag"
  | "cadence"
  | "outside_window"
  | "below_floor_hint"
  | "trading_validity"
  | "price_conflict";

/** `delivery_plan_warnings.PlanWarning`. `detail` is a ready English sentence for ops. */
export interface PlanWarning {
  kind: PlanWarningKind;
  delivery_id: number | null;
  detail: string;
}

/** `escrow_service.ShipGate` — whether a delivery may be transferred, and why not. */
export interface ShipGate {
  ok: boolean;
  escrowed: number;
  planned: number;
  /** English admin sentences; only rendered in admin context (WP-04a C-04a-10). */
  reasons: string[];
}

/** `deal_service.EligibilityCriteriaResponse` — null on a dimension means unrestricted. */
export interface EligibilityCriteriaResponse {
  countries: string[] | null;
  technologies: string[] | null;
  cod_from: string | null;
  cod_until: string | null;
  support: boolean | null;
  ecolabels: string[] | null;
}

/** `deal_service.SliceResponse` — one `(period, volume)` row of the plan. */
export interface SliceResponse {
  id: number;
  period_label: string;
  period_start: string;
  period_end: string;
  volume_mwh: number;
  commitment_id: number | null;
  mandate_id: number | null;
  mandate_name: string | null;
  escrowed_mwh: number;
  is_open: boolean;
}

/**
 * `deal_response_service.ProducerLine` — one producer's line in a delivery's transaction.
 *
 * @property unit_price - What the line invoices at; null when it takes the delivery's price.
 *   Decimal on the wire, so a string.
 * @property mandate_ids - The mandates the line's escrow came through, whose price it carries. Two
 *   or more differently priced ones are a price conflict that holds the shipment, and the line then
 *   carries the highest.
 */
export interface ProducerLine {
  company_id: number;
  company_name: string | null;
  amount: number;
  unit_price: string | null;
  mandate_ids: number[];
}

/**
 * `deal_response_service.MandatePriceResponse` — what one mandate's producers are paid on a
 * delivery. One per backing mandate, plus a price kept for a mandate that stopped backing it.
 *
 * @property unit_price - The mandate's own price, a decimal string; null when it pays the default.
 * @property minimum_price - The highest floor its commitments backing the delivery snapshotted.
 * @property backs_delivery - False for a kept price: unused until the mandate backs the delivery
 *   again, when it applies again.
 */
export interface MandatePriceResponse {
  mandate_id: number;
  mandate_name: string;
  unit_price: string | null;
  minimum_price: string | null;
  backs_delivery: boolean;
}

/**
 * `deal_response_service.SharedProducer` — a company that may sell through two or more mandates of
 * an unshipped delivery, through escrow or as a member of a backing mandate, whether or not their
 * prices differ now: the producer-prices dialog judges a conflict from the prices as typed.
 */
export interface SharedProducer {
  company_id: number;
  company_name: string | null;
  mandate_ids: number[];
}

/**
 * `mandate_prices.ConflictingMandate` — one of the mandates a conflicting company sells through.
 *
 * @property unit_price - What it pays after the fallback; null while the delivery has no price.
 */
export interface ConflictingMandate {
  mandate_id: number;
  mandate_name: string;
  unit_price: string | null;
}

/**
 * `mandate_prices.PriceConflict` — a company whose mandates on one unshipped delivery disagree on
 * its price.
 *
 * @property holds_shipment - Its escrow came through two of the mandates, so the delivery cannot
 *   ship until it is resolved. False for a foreseen conflict: the company is a member of a backing
 *   mandate its escrow has not come through yet.
 */
export interface PriceConflict {
  company_id: number;
  company_name: string;
  mandates: ConflictingMandate[];
  holds_shipment: boolean;
}

/** `deal_service.DeliveryResponse` — one planned shipment. */
export interface DeliveryResponse {
  id: number;
  sales_transaction_id: number;
  /** Null once the delivery's transaction has been voided (soft-deleted). */
  transaction_status: TransactionStatus | null;
  /** A voided delivery's price is the one its voided transaction carries. */
  unit_price: string | null;
  /** Null for an open-ended window — "on or before <window_end>". */
  delivery_window_start: string | null;
  delivery_window_end: string;
  /** Pay-as-produced months carry a nominated target instead of slices. */
  pap_target_mwh: number | null;
  planned_mwh: number;
  escrowed_mwh: number;
  ship_gate: ShipGate;
  warnings: PlanWarning[];
  slices: SliceResponse[];
  producer_lines: ProducerLine[];
  mandate_prices: MandatePriceResponse[];
  /** Foreseen ones included; empty once the delivery has shipped or been voided. */
  price_conflicts: PriceConflict[];
  /** Empty once the delivery has shipped or been voided. */
  shared_producers: SharedProducer[];
  /** The company paid commission on the swap margin. */
  trade_owner: TradeOwnerRef | null;
  /**
   * What every backing mandate left blank in the price dialog pays — the producers' averaged price
   * where a sale pays them one. Null: the delivery's own price.
   */
  default_producer_price: string | null;
  /** Notes about this one shipment, never copied onto its transaction. */
  internal_notes: string | null;
  /** Set only for a shipped or voided delivery the viewer may correct (`can_correct_shipped`). */
  correction_caveats: CorrectionCaveats | null;
  /** Null for a pay-as-produced month, which has no slices. */
  contents: DeliveryContents | null;
}

/**
 * `delivery_contents.DeliveryContents` — what a planned delivery will contain: each backing
 * mandate's slice volume with the mandate's qualities, as the volume picker's summary groups a
 * selection.
 *
 * @property qualification_date - The delivery window's end, which COD age is judged at
 * @property unbacked_mwh - Slice volume no commitment backs yet, so no mandate qualities apply
 */
export interface DeliveryContents {
  qualification_date: string;
  planned_mwh: number;
  by_period: { period_label: string; mwh: number }[];
  mandates: MandateShare[];
  unbacked_mwh: number;
}

/** `delivery_contents.MandateShare` — one backing mandate's volume in a delivery. */
export interface MandateShare {
  mwh: number;
  qualities: MandateQualities;
}

/** `correction_caveats.CorrectionCaveatKind`; each is a `forwards.deals.correction.*` message. */
export type CorrectionCaveatKind =
  | "voided_delivery"
  | "buyer_invoice_replaceable"
  | "buyer_invoice_sent"
  | "completion_invoices_issued"
  | "dashboard_period_moves";

/**
 * `correction_caveats.CorrectionCaveat` — something a history correction leaves as it was.
 *
 * @property invoice_number - Set for the buyer invoice kinds only
 */
export interface CorrectionCaveat {
  kind: CorrectionCaveatKind;
  invoice_number: string | null;
}

/** `correction_caveats.CorrectionCaveats` — what correcting the price, and the window, leaves as it was. */
export interface CorrectionCaveats {
  price: CorrectionCaveat[];
  window: CorrectionCaveat[];
}

/** `deal_response_service.TradeOwnerRef` — a delivery's trade owner (`swap_referrer_company_id`). */
export interface TradeOwnerRef {
  company_id: number;
  company_name: string | null;
}

export interface EscrowByCompany {
  company_id: number;
  company_name: string | null;
  mwh: number;
}

/**
 * `deal_service.CommitmentResponse` — one pull from a mandate with its snapshotted conditions.
 *
 * @property pulled_mwh - What the pull backs right now: the volume of its slices. It only ever
 *   falls — a resize down or a released backing gives volume back — and is 0 for a released
 *   commitment and for pay-as-produced, which claims a window rather than a volume. The deal page
 *   sums the slices itself; the field exists for MCP clients reading the deal as data.
 */
export interface CommitmentResponse {
  id: number;
  created_at: string;
  pulled_mwh: number;
  minimum_price_snapshot: string | null;
  contango_condition_snapshot: ContangoCondition;
  released_at: string | null;
  slices: SliceResponse[];
  escrow_by_company: EscrowByCompany[];
  /** How much the pull took past its mandate's sales strategy, measured at the pull — 0 within it. */
  beyond_strategy_mwh: number;
  /**
   * The mandate's sales strategy at the pull; null for pay-as-produced and for pulls that predate
   * strategies.
   */
  sales_plan_snapshot: SalesPlan | null;
}

/** `deal_service.CommitmentGroup` — a mandate appears once however often it was pulled from. */
/** `deal_response_service.MandateMemberRef` — a member company of a backing mandate, as the pull form names it. */
export interface MandateMemberRef {
  company_id: number;
  company_name: string;
}

export interface CommitmentGroup {
  mandate_id: number;
  mandate_name: string;
  /** Null for a pay-as-produced mandate, which states no width. */
  entry_width: PeriodWidth | null;
  commitments: CommitmentResponse[];
  /**
   * The mandate's member companies, the owners a pull may draw from. Carried only when the viewer may
   * move escrow, read under the viewer's own scope; empty otherwise.
   */
  members: MandateMemberRef[];
}

/** `deal_service.FreeEscrowRow` — escrow with no live delivery behind it, awaiting a return. */
export interface FreeEscrowRow {
  attribution_id: number;
  portfolio_item_id: number;
  company_id: number;
  company_name: string | null;
  count: number;
  period_label: string | null;
}

/** `deal_service.InfeasibilityFlag` — a period a backing mandate cannot cover. */
export interface InfeasibilityFlag {
  mandate_id: number;
  mandate_name: string;
  period_label: string;
  shortfall_mwh: number;
}

/** `eligibility_service.DriftEntry` — a committed device that stopped fitting the deal. */
export interface DriftEntry {
  deal_id: number;
  mandate_id: number;
  commitment_id: number;
  device_id: number;
  device_name: string | null;
  failures: FailedCriterion[];
}

/** `deal_service.DealFlags` — everything the deal page badges. None of them blocks a read. */
export interface DealFlags {
  open_volume_mwh: number;
  /** Delivery id → committed-but-not-escrowed MWh; keys travel as strings in JSON. */
  escrow_shortfall_by_delivery: Record<string, number>;
  infeasibility: InfeasibilityFlag[];
  /** Planned volume less the stored deal volume; null when there is no drift or no stored volume. */
  volume_drift_mwh: number | null;
  is_stale_draft: boolean;
  drift_report: DriftEntry[];
  free_escrow_mwh: number;
}

/** `deal_service.DealCapabilities` — what this actor may do, so the page disables rather than hides. */
export interface DealCapabilities {
  /**
   * Whether this session may change the deal at all; every other `can_*` is false without it, and it
   * alone offers the metadata edit and the escrow moves. Only a super admin working as themselves
   * manages deals; a `sales_admin` reads them (2026-10-12).
   */
  can_manage: boolean;
  can_confirm: boolean;
  /** A ready English sentence; shown beside the disabled Confirm button. */
  confirm_blocked_reason: string | null;
  can_cancel: boolean;
  /**
   * Whether this session may return the confirmed deal to draft: a super admin, a fixed-volume deal,
   * and nothing escrowed, shipped or invoiced to the buyer yet.
   */
  can_unconfirm: boolean;
  can_edit_criteria: boolean;
  can_manage_commitments: boolean;
  /**
   * Whether this session may write the pay-as-produced schedule — the nominations, the tolerance
   * and each month's sell price, which are saved together.
   */
  can_edit_pap_schedule: boolean;
  /**
   * Whether this session may correct a shipped or voided delivery's window and price (a
   * pay-as-produced month's price only), on a completed or cancelled deal too — a super admin's.
   */
  can_correct_shipped: boolean;
}

/** `deal_service.DealResponse` — the whole deal page in one object. */
export interface DealResponse {
  id: number;
  type: DealType;
  status: DealStatus;
  name: string;
  /** Copied onto every unshipped delivery's transaction: printed on the producers' invoices. */
  description: string | null;
  /** Staff notes, never copied onto a transaction. */
  internal_notes: string | null;
  confirmed_at: string | null;
  signed_at: string | null;
  volume_mwh: number | null;
  counterparty_name: string | null;
  counterparty_country: string | null;
  counterparty_eecs_account_number: string | null;
  counterparty_emails: string | null;
  counterparty_registration_authority: DealRegistrationAuthority;
  counterparty_global_company_id: number | null;
  contract_links: string | null;
  transaction_link: string | null;
  /** Comma-separated history of offering-sheet URLs, newest last. */
  volumes_specification_links: string | null;
  criteria: EligibilityCriteriaResponse;
  pap_window_start: string | null;
  pap_window_end: string | null;
  pap_volume_tolerance: string | null;
  created_at: string;
  updated_at: string;
  /** The stamp a plan save has to carry back; every plan-affecting write bumps it. */
  plan_version: number;
  flags: DealFlags;
  deliveries: DeliveryResponse[];
  commitments: CommitmentGroup[];
  free_escrow: FreeEscrowRow[];
  capabilities: DealCapabilities;
}

/** `PATCH /deals/<id>` — only the fields actually sent are written. */
export interface DealMetadataRequest {
  name?: string;
  description?: string | null;
  internal_notes?: string | null;
  signed_at?: string | null;
  volume_mwh?: number | null;
  counterparty_name?: string | null;
  counterparty_country?: string | null;
  counterparty_eecs_account_number?: string | null;
  counterparty_emails?: string | null;
  counterparty_registration_authority?: DealRegistrationAuthority | null;
  counterparty_global_company_id?: number | null;
  /** Free text, usually comma-separated links, as on spot transactions. */
  contract_links?: string | null;
  /** Comma-separated offering-sheet links, oldest first; the generator appends, this prunes. Free text otherwise. */
  volumes_specification_links?: string | null;
  /** One http or https link, or the request is a 400 (`isSingleWebLinkOrBlank`). */
  transaction_link?: string | null;
}

/** `criteria_conflicts.ConflictKind`; the `shipped_*` kinds only warn, every other kind blocks the change. */
export type CriteriaConflictKind =
  | "backing"
  | "escrow"
  | "free_escrow"
  | "unresolved_escrow"
  | "shipped_backing"
  | "shipped_escrow";

/** `criteria_conflicts.CriteriaConflict` — one thing a criteria change conflicts with. */
export interface CriteriaConflict {
  kind: CriteriaConflictKind;
  /** A ready English sentence; a blocking one says how to clear it. */
  message: string;
  delivery_id: number | null;
  slice_id: number | null;
  mandate_id: number | null;
}

/**
 * `deal_service.CriteriaUpdateResult` — the change's conflicts. A save never answers with blocking
 * ones: it is refused with them instead.
 */
export interface CriteriaUpdateResult {
  deal: DealResponse;
  blocking: CriteriaConflict[];
  warnings: CriteriaConflict[];
  dry_run: boolean;
}

/** A dry run's answer, kept with the criteria it was asked about so a changed draft can tell it is stale. */
export interface CriteriaPreview {
  body: EligibilityCriteriaRequest;
  blocking: CriteriaConflict[];
  warnings: CriteriaConflict[];
}

/** `deal_plan_state_service.PlanPeriodState` — one production period of one delivery. */
export interface PlanPeriodState {
  /** A period label: `2026`, `2026-H1`, `2026-Q1` or `2026-03`. */
  period: string;
  /** Backed plus open. The server writes `planned_mwh - backed` as the delivery's open volume. */
  planned_mwh: number;
}

/**
 * `deal_plan_state_service.PlanDeliveryState` — one planned shipment as the editor states it.
 *
 * `id: null` creates the delivery. Per-producer invoicing prices are not part of the plan and a
 * save leaves them alone.
 *
 * @property delivery_window_start - Null or left out: the window is open before its end date
 * @property unit_price - Required by the backend (a missing one is a 400); null clears the price
 */
export interface PlanDeliveryState {
  id: number | null;
  delivery_window_start?: string | null;
  delivery_window_end: string;
  unit_price: string | null;
  periods: PlanPeriodState[];
}

/**
 * `deal_plan_state_service.PlanStateRequest` — the plan the deal should end up with.
 *
 * A delivery of the deal this list leaves out is removed, which is why `plan_version` travels with
 * it: a save composed against an older plan answers 409 instead of deleting what it never saw.
 */
export interface PlanStateRequest {
  plan_version: number;
  deliveries: PlanDeliveryState[];
}

/**
 * `producer_price_service.ProducerPricesRequest` — one delivery's producer prices, whole, and its
 * trade owner.
 *
 * @property mandate_prices - Mandate id -> what that mandate's producers are paid, a decimal string,
 *   kept as sent even when it equals the default. Stated whole over the backing mandates and the
 *   prices kept for mandates that stopped backing the delivery: a mandate left out follows the
 *   default producer price, or the delivery's own price when there is none.
 * @property trade_owner_company_id - The swap-referrer candidate paid commission on the margin.
 *   Omitted leaves the trade owner as it is; null clears it.
 */
export interface ProducerPricesRequest {
  /** The deal's stamp as the dialog was opened; a stale one is a 409 the dialog merges against. */
  plan_version: number;
  mandate_prices: Record<number, string>;
  /** What a mandate left out of `mandate_prices` pays. Stated whole like the map: null clears it. */
  default_price: string | null;
  trade_owner_company_id?: number | null;
}

/** `delivery_notes_service.DeliveryNotesRequest` — a delivery's staff-only notes, whole; null or blank clears them. */
export interface DeliveryNotesRequest {
  internal_notes: string | null;
}

/**
 * `deal_plan_state_service.PlanDeliveryOutcome` — what one request entry did.
 *
 * Keyed by `index` into the request's `deliveries`, the only key a delivery being created has.
 * `delivery_id` is provisional on a dry run: the rolled-back savepoint handed it out.
 */
export interface PlanDeliveryOutcome {
  index: number;
  delivery_id: number;
  /** English sentences of what was, or on a dry run would be, written. Empty when nothing changed. */
  changes: string[];
  warnings: PlanWarning[];
}

/** `deal_plan_state_service.PlanApplyResult` — identical whether the plan was written or previewed. */
/** A delivery the request left out and the save removed (or would remove, on a dry run). */
export interface RemovedDelivery {
  delivery_id: number;
  delivery_window_end: string;
}

export interface PlanApplyResult {
  deal: DealResponse;
  deliveries: PlanDeliveryOutcome[];
  removed: RemovedDelivery[];
  /** Warnings about deliveries the request did not state — a shipped one, usually. */
  warnings: PlanWarning[];
  dry_run: boolean;
}

/** `slice_reshape_service.MoveResult`. `source_slice_id` is null when the whole slice moved. */
export interface MoveResult {
  source_slice_id: number | null;
  target_slice_id: number;
  moved_volume_mwh: number;
  moved_escrow_mwh: number;
}

/** `commitment_service.ResizeResult`. `new_commitment_id` is set when growing. */
export interface ResizeResult {
  slice_id: number;
  commitment_id: number;
  volume_mwh: number;
  opened_volume_mwh: number;
  new_commitment_id: number | null;
  /** A grow is a new commitment, so it warns like one. Empty when shrinking. */
  warnings: string[];
}

/** `eligibility_service.MandateFit` — one flat list; `eligible` is `failures` being empty. */
export interface MandateFit {
  mandate_id: number | null;
  eligible: boolean;
  failures: FailedCriterion[];
}

/** `commitment_service.CommitmentMandateRow` — one result of the add-commitment type-ahead. */
export interface CommitmentMandateRow {
  mandate_id: number;
  name: string;
  /** Never null here: pay-as-produced mandates are not offered. */
  entry_width: PeriodWidth | null;
  minimum_price: string | null;
  contango_condition: ContangoCondition;
  /** What the mandate lists over the deal's open periods — guidance, not the per-take cap. */
  listed_volume_mwh: number;
  /**
   * Per open-slice period label, what a take may still draw — `sellable_mwh` *is* the per-take
   * cap.
   */
  capacity_by_period: Record<string, PeriodCapacity>;
  sales_plan: SalesPlan;
  /** The fit over every open period at once: false as soon as one period fails. */
  fit: MandateFit;
  /**
   * The same judgement per open-period label. A take is judged per period on the backend, and
   * OK-power "new" expires with the device's age, so a mandate can back 2026 and fail 2032.
   */
  fit_by_period: Record<string, MandateFit>;
}

/** The mutating deal routes that wrap `DealResponse` alongside what the operation returned. */
export interface MoveSliceResult {
  deal: DealResponse;
  move: MoveResult;
}

export interface NarrowSliceResult {
  deal: DealResponse;
  slice_ids: number[];
}

export interface ResizeCommitmentResult {
  deal: DealResponse;
  resize: ResizeResult;
  /** Null unless the grow overrode the sales strategy and needed to. */
  beyond_sales_strategy: BeyondStrategyRow | null;
}

export interface AddCommitmentResult {
  deal: DealResponse;
  commitment_id: number;
  /** What the mandate now looks unable to cover. Advisory — the commitment was written either way. */
  warnings: string[];
  /** Null unless the commitment overrode the sales strategy and needed to. */
  beyond_sales_strategy: BeyondStrategyRow | null;
}

/** `offering_sheet_service.OfferingSheetResponse` — the sheet's URL is a Google Sheet link. */
export interface OfferingSheetResult {
  deal: DealResponse;
  offering_sheet_url: string;
}

// --- admin escrow moves (WP-06) ---------------------------------------------------------------

/** `escrow_service.EscrowAttributionRow` — one escrowed certificate, as the return picker lists it. */
export interface EscrowAttributionRow {
  attribution_id: number;
  portfolio_item_id: number;
  certificate_id: number;
  company_id: number;
  company_name: string;
  device_id: number | null;
  device_name: string | null;
  count: number;
  production_period_label: string;
  commitment_id: number;
  slice_id: number | null;
  slice_period_label: string | null;
  delivery_id: number | null;
  delivery_window_end: string | null;
  transaction_status: TransactionStatus | null;
}

/** `escrow_service.EscrowMoveItemResult` — registry transfers fail per item, not per batch. */
export interface EscrowMoveItemResult {
  attribution_id: number | null;
  /** The item the selection named — a pull's source item, a return's escrowed item — never one the move split off or created. */
  portfolio_item_id: number;
  count: number;
  ok: boolean;
  reason: string | null;
}

export interface EscrowMoveResult {
  items: EscrowMoveItemResult[];
}

/** `POST /deals/<id>/escrow/return` — which attributions go back, and how much of each. */
export interface EscrowReturnRequest {
  selection: { attribution_id: number; count: number }[];
}

/** `POST /deals/<id>/escrow/pull` — the commitment is named in the body, not the path. */
export interface EscrowPullRequest {
  commitment_id: number;
  company_id: number;
  source_portfolio_id: number;
  items: { portfolio_item_id: number; count: number }[];
}

// --- pay-as-produced monitoring (WP-07) -------------------------------------------------------

/** One nominated month of a pay-as-produced deal, as the monitoring table renders it. */
export interface PapMonthStatus {
  delivery_id: number;
  /** `YYYY-MM`. */
  month: string;
  target_mwh: number;
  escrowed_mwh: number;
  status: "pending" | "within" | "over" | "under" | "delivered";
  transaction_status: TransactionStatus | null;
  deliverable: boolean;
  /** Why the month cannot ship — the ship gate's own sentences, shown beside the disabled button. */
  reasons: string[];
  sales_transaction_id: number;
}

/** `GET /api/admin/forwards/deals/<id>/pay-as-produced/monitoring`. */
export interface PapMonitoringResponse {
  deal_id: number;
  window_start: string;
  window_end: string;
  /** A fraction as a decimal string — `"0.1"` is ±10 %. */
  volume_tolerance: string;
  months: PapMonthStatus[];
}

/**
 * `PATCH /api/admin/forwards/deals/<id>/pay-as-produced` — any field alone is a valid edit, and a
 * month a map leaves out keeps its value.
 *
 * @property plan_version - The deal's stamp as the schedule was read; a stale one is a 409 whose
 *   `error_data.current_version` the card merges against.
 * @property monthly_prices - Month label to sell price (€/MWh, a decimal string above zero).
 */
export interface PapScheduleUpdateRequest {
  plan_version: number;
  monthly_targets?: Record<string, number>;
  volume_tolerance?: string;
  monthly_prices?: Record<string, string>;
}
